import { useCallback, useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import _ from "lodash";
import { useLocation } from "react-router";
import { Link as RouterLink } from "react-router-dom";

import { PanelLeftClose } from "lucide-react";

import FreshpaintLogoLight from "@/components/FpLogoLight";
import FreshpaintLogoSmall from "@/components/FpLogoSmall";
import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import { LocalStorageProject } from "@/reducers/environments";
import NavBarSkeleton from "./NavBarSkeleton";
import { NavigationEnvironmentPicker } from "./EnvironmentPicker";
import usePermissions from "@/access-control/queries/usePermissions";
import { RBACAccessLevel, RBACDomain } from "@/access-control/types";
import { useAccountEntitlements } from "@/entitlements/queries/useAccountEntitlements";
import useAccountMetadata from "@/hooks/useAccountMetadata";
import useFeatureFlags from "@/hooks/useFeatureFlag";
import useUsers from "@/hooks/useUsers";
import useCurrentProject from "@/hooks/use-project";
import useCanViewDestinationRanks from "@/admin/useCanViewDestinationRanks";
import useWTMURLs from "@/trackers/useWTMURLs";
import FreshpaintUser from "@/components/users/FreshpaintUser";
import { NavBarProps } from "./types";
import { renderNavItems } from "./renderingUtils";
import { NavScroll, NavScrollProvider } from "./navScrollContext";
import { animateScrollTop } from "./scrollUtils";
import { useNavScrollPersistence } from "./hooks/useNavScrollPersistence";
import {
  getAnalyticsNavItems,
  getAudienceNavItems,
  getConsentManagementNavItems,
  getEventNavItems,
  getFreshpaintAINavItems,
  getInsightsNavItems,
  getIntegrationNavItems,
  getInternalToolsNavItems,
  getAccountToolsNavItems,
  getPrivacyDashboardNavItems,
  getSettingsNavItems,
  getSetupNavItems,
  getShortLinksNavItems,
  getSystemStatusNavItems,
  getVideoNavItems,
  getWebTrackerMonitoringNavItems,
  getWTMAdminNavItems,
} from "./navigationItems";
import { useUserHasAccess } from "@/hooks/useUserHasAccess";
import { useUserHasAccessToAny } from "@/hooks/useUserHasAccessToAny";

const NavBar = ({
  className,
  drawerCollapse,
  setDrawerCollapse,
  ...rest
}: NavBarProps) => {
  const location = useLocation();
  const accountMetadata = useAccountMetadata();
  const usersState = useUsers();
  const { isLoading: permsLoading } = usePermissions();
  const { isLoading: entitlementsLoading } = useAccountEntitlements();

  const scrollContainerRef = useRef<HTMLElement | null>(null);

  const cancelNavScrollRef = useRef<(() => void) | null>(null);
  useEffect(() => () => cancelNavScrollRef.current?.(), []);

  const navScroll = useMemo<NavScroll>(
    () => ({
      containerRef: scrollContainerRef,
      scrollTo: (targetTop: number) => {
        if (!scrollContainerRef.current) {
          return;
        }
        cancelNavScrollRef.current?.();
        cancelNavScrollRef.current = animateScrollTop(
          scrollContainerRef.current,
          targetTop,
        );
      },
    }),
    [],
  );

  const accountMetadataLoaded = accountMetadata.loaded ?? false;
  const trackScrollContainer = useNavScrollPersistence(accountMetadataLoaded);

  // The persistence hook needs the element as state so its effects follow the
  // DOM, while NavScroll hands NavItem a stable ref it can read during a click.
  const setScrollContainer = useCallback(
    (node: HTMLElement | null) => {
      scrollContainerRef.current = node;
      trackScrollContainer(node);
    },
    [trackScrollContainer],
  );

  const {
    systemStatusNavItems,
    freshpaintAINavItems,
    setupNavItems,
    eventNavItems,
    integrationNavItems,
    insightsNavItems,
    analyticsNavItems,
    audienceNavItems,
    consentManagementNavItems,
    privacyDashboardNavItems,
    shortLinksNavItems,
    videoNavItems,
    webTrackerMonitoringNavItems,
    internalToolsNavItems,
    accountToolsNavItems,
    wtmAdminNavItems,
    settingsNavItems,
  } = useNavItems();

  if (usersState.loading || permsLoading || entitlementsLoading) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <div
        className={clsx(
          "bg-nav-shell flex h-[68px] items-center justify-between transition-[padding] duration-550 ease-[cubic-bezier(0.16,1,0.3,1)]",
          drawerCollapse ? "px-4" : "pr-4 pl-6",
        )}
      >
        <RouterLink to="/" className="relative flex h-9 items-center">
          <span
            className={clsx(
              "absolute left-0 transition-opacity duration-550 ease-[cubic-bezier(0.16,1,0.3,1)]",
              drawerCollapse ? "opacity-100" : "opacity-0",
            )}
          >
            <FreshpaintLogoSmall height="32" />
          </span>
          <span
            className={clsx(
              "transition-opacity duration-550 ease-[cubic-bezier(0.16,1,0.3,1)]",
              drawerCollapse ? "opacity-0" : "opacity-100",
            )}
          >
            <FreshpaintLogoLight height="32" />
          </span>
        </RouterLink>
        <Tooltip content="Collapse navigation" triggerProps={{ asChild: true }}>
          <button
            type="button"
            onClick={() => setDrawerCollapse(true)}
            aria-label="Collapse navigation"
            aria-hidden={drawerCollapse}
            tabIndex={drawerCollapse ? -1 : 0}
            className={clsx(
              "hover:bg-nav-hover flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-550 ease-[cubic-bezier(0.16,1,0.3,1)]",
              drawerCollapse && "w-0 overflow-hidden opacity-0",
            )}
          >
            <PanelLeftClose size={18} />
          </button>
        </Tooltip>
      </div>

      <NavigationEnvironmentPicker collapsed={drawerCollapse} />
      <div className="bg-nav-shell border-b border-white/15" />
      <nav
        {...rest}
        className={clsx(
          "scrollbar bg-nav-shell flex flex-1 flex-col overflow-y-auto p-1.5 pb-1",
          className,
        )}
        ref={setScrollContainer}
      >
        {accountMetadataLoaded ? (
          <NavScrollProvider value={navScroll}>
            {renderNavItems({
              collapsed: drawerCollapse,
              items: freshpaintAINavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: setupNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: eventNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: integrationNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: analyticsNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: insightsNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: audienceNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: consentManagementNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: privacyDashboardNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: shortLinksNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: videoNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: webTrackerMonitoringNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: accountToolsNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: internalToolsNavItems,
              pathname: location.pathname,
            })}
            {renderNavItems({
              collapsed: drawerCollapse,
              items: wtmAdminNavItems,
              pathname: location.pathname,
            })}
          </NavScrollProvider>
        ) : (
          <NavBarSkeleton />
        )}
      </nav>
      {accountMetadataLoaded && (
        <div className="bg-nav-shell flex flex-col border-t border-white/15 p-1.5">
          {renderNavItems({
            collapsed: drawerCollapse,
            items: systemStatusNavItems,
            pathname: location.pathname,
          })}
          {renderNavItems({
            collapsed: drawerCollapse,
            items: settingsNavItems,
            pathname: location.pathname,
          })}
        </div>
      )}
    </div>
  );
};

export function useNavItems() {
  const accountMetadata = useAccountMetadata();
  const flag = useFeatureFlags();
  const wtmURLs = useWTMURLs();
  const usersState = useUsers();
  const { data: accountEntitlements } = useAccountEntitlements();

  const currentProject = useCurrentProject();
  const hasGlobalAllowListEntries = (() => {
    if (!currentProject.loaded) {
      return false;
    }
    const p = currentProject as LocalStorageProject;
    return (
      !_.isEmpty(p.eventPropertySensitivityLevels) ||
      !_.isEmpty(p.userPropertySensitivityLevels) ||
      !_.isEmpty(p.groupPropertySensitivityLevels)
    );
  })();

  const canViewDestinationRanks = useCanViewDestinationRanks();
  const canEditEvents = useUserHasAccess(
    RBACDomain.eventLibrary,
    RBACAccessLevel.write,
  );

  const canEditDestinations = useUserHasAccess(
    RBACDomain.destinationConfiguration,
    RBACAccessLevel.write,
  );
  const canAccessVideo = useUserHasAccess(
    RBACDomain.video,
    RBACAccessLevel.write,
  );

  const canAccessAudiences = useUserHasAccessToAny(
    RBACDomain.audiences,
    RBACAccessLevel.read,
    RBACAccessLevel.write,
  );

  const canAccessOfflineAttributions = useUserHasAccess(
    RBACDomain.offlineAttributions,
    RBACAccessLevel.read,
  );
  const canAccessWtm = useUserHasAccess(
    RBACDomain.webTrackerMonitoring,
    RBACAccessLevel.write,
  );
  const canAccessUserProfiles = useUserHasAccess(
    RBACDomain.userProfiles,
    RBACAccessLevel.read,
  );

  const hasAdminAccess =
    !usersState.loading &&
    new FreshpaintUser(usersState.currentUser).hasAdminAccess();

  const newUploader = flag("tempNewUploader");
  const systemStatusNavItems = getSystemStatusNavItems(hasAdminAccess);
  const freshpaintAINavItems = getFreshpaintAINavItems();
  const setupNavItems = getSetupNavItems(newUploader);
  const eventNavItems = getEventNavItems(
    accountMetadata,
    !!canEditEvents,
    hasGlobalAllowListEntries,
  );
  const integrationNavItems = getIntegrationNavItems(
    accountMetadata,
    flag,
    !!canEditDestinations,
    !!canAccessAudiences,
    accountEntitlements,
  );
  const analyticsNavItems = getAnalyticsNavItems(flag);
  const insightsNavItems = getInsightsNavItems(
    flag,
    !!canAccessOfflineAttributions,
  );
  const audienceNavItems = getAudienceNavItems(
    flag,
    !!canAccessAudiences,
    !!canAccessUserProfiles,
  );
  const consentManagementNavItems = getConsentManagementNavItems(flag);
  const privacyDashboardNavItems = getPrivacyDashboardNavItems();
  const shortLinksNavItems = getShortLinksNavItems();
  const videoNavItems = getVideoNavItems(accountEntitlements, !!canAccessVideo);
  const webTrackerMonitoringNavItems = getWebTrackerMonitoringNavItems(
    wtmURLs,
    flag,
    accountMetadata,
    !!canAccessWtm,
  );
  const accountToolsNavItems = getAccountToolsNavItems();
  const internalToolsNavItems = getInternalToolsNavItems(
    canViewDestinationRanks,
  );
  const wtmAdminNavItems = getWTMAdminNavItems();
  const settingsNavItems = getSettingsNavItems();

  return {
    systemStatusNavItems,
    freshpaintAINavItems,
    setupNavItems,
    eventNavItems,
    integrationNavItems,
    insightsNavItems,
    analyticsNavItems,
    audienceNavItems,
    consentManagementNavItems,
    privacyDashboardNavItems,
    shortLinksNavItems,
    videoNavItems,
    webTrackerMonitoringNavItems,
    internalToolsNavItems,
    accountToolsNavItems,
    wtmAdminNavItems,
    settingsNavItems,
  };
}

export default NavBar;
